import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts'
import { AppDispatch, RootState } from 'store'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ChartCard } from '../components/ChartCard'
import { ChartTooltip } from '../components/ChartTooltip'
import { AnalyticsTable, RateCellRenderer, RedValueRenderer } from '../components/AnalyticsTable'
import { SubTabBar } from '../components/SubTabBar'
import {
  KpiRow,
  StatusBarWrapper,
  StatusBar,
  StatusSegment,
  StatusLegend,
  LegendItem,
  LegendDot,
  ChartCardWrapper,
  ChartCardTitle,
} from '../HrAnalytics.styled'
import {
  clearHrAnalytics,
  getAttendanceByDepartment,
  getAttendanceByRole,
  getAttendanceRateTrend,
  getAttendanceStatusDistribution,
  getAttendanceSummary,
} from 'features/hrAnalytics/hrAnalytics.slice'
import { tokens } from 'styling/tokens'

const KpiIcons: Record<string, React.ReactNode> = {
  person: <svg viewBox='0 0 24 24'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
  chart: <svg viewBox='0 0 24 24'><path d='M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z' /></svg>,
}

const DEPT_COLUMNS = [
  { key: 'name', label: 'Department', sortable: true, width: '20%' },
  { key: 'expected', label: 'Expected', sortable: true, width: '20%' },
  { key: 'present', label: 'Present', sortable: true, width: '20%' },
  {
    key: 'absent',
    label: 'Absent',
    sortable: true,
    width: '15%',
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'rate',
    label: 'Rate',
    sortable: true,
    width: '25%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const STATUS_META: Record<number, { label: string; color: string }> = {
  1: { label: 'Present', color: tokens.status.success.fg },
  2: { label: 'Absent', color: tokens.status.danger.fg },
  3: { label: 'Leave', color: tokens.accent.base },
  4: { label: 'Excused', color: tokens.status.info.fg },
  5: { label: 'Holiday Out', color: tokens.status.warning.fg },
  6: { label: 'Sick Leave', color: tokens.status.success.fg },
  7: { label: 'Off Day', color: tokens.text.muted },
  8: { label: 'Training', color: tokens.status.success.fg },
  9: { label: 'Vacation', color: tokens.status.warning.fg },
  10: { label: 'Maternity', color: tokens.status.danger.fg },
  11: { label: 'Holiday In', color: tokens.text.secondary },
}

const UNKNOWN_STATUS = { label: 'Unknown', color: tokens.text.muted }

const PERIOD_MAP: Record<string, 1 | 2 | 3> = {
  Day: 1,
  Week: 2,
  Month: 3,
}

const toPercent = (value: number | null) => {
  if (value == null) return 0
  return value > 1 ? Math.round(value * 10) / 10 : Math.round(value * 1000) / 10
}

const toPercentLabel = (value: number | null) => {
  if (value == null) return '—'
  const pct = value > 1 ? value : value * 100
  return `${pct.toFixed(1)}%`
}

const formatChange = (value: number | null) => {
  if (value == null) return undefined
  const pct = Math.abs(value) > 1 ? value : value * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}

const formatShortDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}



export const AttendanceTab = ({ startDate, endDate }: ITab) => {
  const dispatch = useDispatch<AppDispatch>()
  const [period, setPeriod] = useState('Day')
  const [tableTab, setTableTab] = useState('department')

  const {
    attendanceSummary,
    attendanceByDepartment,
    attendanceByRole,
    attendanceRateTrend,
    attendanceStatusDistribution,
  } = useSelector((state: RootState) => state.hrAnalytics)

  useEffect(() => {
    if (!startDate || !endDate) return
    const params = { startDate, endDate }
    dispatch(getAttendanceSummary(params))
    dispatch(getAttendanceByDepartment(params))
    dispatch(getAttendanceByRole(params))
    dispatch(getAttendanceStatusDistribution(params))
    return () => {
      dispatch(clearHrAnalytics())
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (!startDate || !endDate) return
    dispatch(getAttendanceRateTrend({
      startDate,
      endDate,
      period: PERIOD_MAP[period] ?? 1,
    }))
  }, [startDate, endDate, period])

  const kpis = useMemo(
    () => [
      {
        value: (attendanceSummary?.expected ?? 0).toLocaleString(),
        label: 'EXPECTED',
        sublabel: 'Employee Days',
        accentColor: tokens.status.info.fg,
        iconKey: 'person',
      },
      {
        value: (attendanceSummary?.present ?? 0).toLocaleString(),
        label: 'PRESENT',
        sublabel: `${toPercentLabel(attendanceSummary?.presentPercentOfExpected ?? null)} of expected`,
        accentColor: tokens.status.success.fg,
        iconKey: 'checkCircle',
      },
      {
        value: (attendanceSummary?.absent ?? 0).toLocaleString(),
        label: 'ABSENT',
        sublabel: `${toPercentLabel(attendanceSummary?.absentPercentOfExpected ?? null)} of expected`,
        accentColor: tokens.accent.base,
        iconKey: 'warning',
      },
      {
        value: toPercentLabel(attendanceSummary?.attendanceRate ?? null),
        label: 'ATTENDANCE RATE',
        trend: attendanceSummary?.attendanceRateChange != null
          ? {
            value: formatChange(attendanceSummary.attendanceRateChange) || '',
            positive: (attendanceSummary.attendanceRateChange ?? 0) >= 0,
          }
          : undefined,
        accentColor: tokens.status.success.fg,
        iconKey: 'chart',
      },
    ],
    [attendanceSummary],
  )

  const trendData = useMemo(
    () =>
      attendanceRateTrend.map((point) => ({
        date: formatShortDate(point.date),
        rate: toPercent(point.rate),
      })),
    [attendanceRateTrend],
  )

  const statusDistribution = useMemo(
    () =>
      attendanceStatusDistribution.map((item) => {
        const meta = item.type != null ? STATUS_META[item.type] : UNKNOWN_STATUS
        return {
          label: meta?.label ?? UNKNOWN_STATUS.label,
          percent: toPercent(item.percent),
          color: meta?.color ?? UNKNOWN_STATUS.color,
        }
      }),
    [attendanceStatusDistribution],
  )

  const deptRows = useMemo(
    () =>
      attendanceByDepartment.map((d) => ({
        name: d.departmentName,
        expected: d.expected,
        present: d.present,
        absent: d.absent,
        rate: toPercent(d.attendanceRate),
      })),
    [attendanceByDepartment],
  )

  const roleRows = useMemo(
    () =>
      attendanceByRole.map((r) => ({
        name: r.roleName,
        expected: r.expected,
        present: r.present,
        absent: r.absent,
        rate: toPercent(r.attendanceRate),
      })),
    [attendanceByRole],
  )

  const tableData = tableTab === 'department' ? deptRows : roleRows

  return (
    <>
      <KpiRow>
        {kpis.map((kpi, idx) => (
          <MetricCard
            key={idx}
            layout='inline'
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            trend={kpi.trend}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Attendance Rate Trend'
        periods={['Day', 'Week', 'Month']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Rate', color: tokens.accent.base },
          { label: '85% Target', color: tokens.status.danger.fg, dashed: true },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
            <XAxis dataKey='date' tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <Tooltip cursor={{ stroke: tokens.border.strong, strokeWidth: 1 }} content={<ChartTooltip formatter={(v: number) => `${v}%`} />} />
            <ReferenceLine y={85} stroke={tokens.status.danger.fg} strokeDasharray='8 8' strokeWidth={2} />
            <Line
              type='monotone'
              dataKey='rate'
              name='Attendance Rate'
              stroke={tokens.accent.base}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: tokens.accent.base }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCardWrapper>
        <ChartCardTitle>Status Distribution</ChartCardTitle>
        <StatusBarWrapper>
          <StatusBar>
            {statusDistribution.map((status) => (
              <StatusSegment
                key={status.label}
                percent={status.percent}
                color={status.color}
              >
                {status.percent > 5 ? `${status.percent}%` : ''}
              </StatusSegment>
            ))}
          </StatusBar>
          <StatusLegend>
            {statusDistribution.map((status) => (
              <LegendItem key={status.label}>
                <LegendDot color={status.color} />
                {status.label}
              </LegendItem>
            ))}
          </StatusLegend>
        </StatusBarWrapper>
      </ChartCardWrapper>

      <SubTabBar
        tabs={[
          { key: 'department', label: 'By Department' },
          { key: 'role', label: 'By Role' },
        ]}
        activeTab={tableTab}
        onTabChange={setTableTab}
      />

      <AnalyticsTable columns={DEPT_COLUMNS} data={tableData} />
    </>
  )
}


interface ITab {
  startDate: Date
  endDate: Date
}